import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  useColorModeValue,
  Image,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import DefaultAuth from 'layouts/auth/Default';
import illustration from 'assets/img/tablet-login-concept-illustration_114360-7963.avif';
import { useResetPassword } from './hooks/useResetPassword';
import { Spinner } from '@chakra-ui/react';

function ResetPassword() {
  const navigate = useNavigate();
  const { mutate: resetPassword, isPending } = useResetPassword();

  const [mobile, setMobile] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [errors, setErrors] = useState({});

  const cardBg = useColorModeValue('white', 'gray.800');
  const textPrimary = useColorModeValue('gray.600', 'gray.300');
  const textSecondary = useColorModeValue('gray.500', 'gray.300');

  const handleReset = (e) => {
    e.preventDefault();
    let newErrors = {};

    if (!mobile) newErrors.mobile = 'Mobile number is required';
    if (!oldPassword) newErrors.oldPassword = 'Old password is required';
    if (!newPassword) newErrors.newPassword = 'New password is required';
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    resetPassword(
      { mobile, oldPassword, newPassword, confirmPassword },
      {
        onSuccess: () => {
          navigate('/auth/sign-in');
        },
      },
    );
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      {isPending && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg="blackAlpha.600"
          zIndex="9999"
          align="center"
          justify="center"
        >
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Flex>
      )}
      <Flex align="center" justify="flex-start" h="100vh" w="100%">
        <Box
          bg={cardBg}
          p={{ base: 6, md: 10 }}
          borderRadius="lg"
          boxShadow="lg"
          w={{ base: '100%', md: '400px' }}
        >
          <Image
            src="/logo.png"
            alt="Logo"
            mx="auto"
            maxH="120px"
            p="10px"
          />

          <Text fontSize="sm" color={textSecondary} mb={6} textAlign="center">
            Reset your password using your mobile number
          </Text>

          <form onSubmit={handleReset}>
            {/* MOBILE */}
            <FormControl mb={4} isInvalid={!!errors.mobile}>
              <FormLabel fontSize="sm" color={textPrimary}>
                Mobile Number
              </FormLabel>
              <Input
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  setErrors({ ...errors, mobile: '' });
                }}
              />
              {errors.mobile && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  {errors.mobile}
                </Text>
              )}
            </FormControl>

            {/* OLD PASSWORD */}
            <FormControl mb={4} isInvalid={!!errors.oldPassword}>
              <FormLabel fontSize="sm" color={textPrimary}>
                Old Password
              </FormLabel>
              <InputGroup>
                <Input
                  type={showOld ? 'text' : 'password'}
                  placeholder="Enter old password"
                  value={oldPassword}
                  onChange={(e) => {
                    setOldPassword(e.target.value);
                    setErrors({ ...errors, oldPassword: '' });
                  }}
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={() => setShowOld(!showOld)}>
                    {showOld ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              {errors.oldPassword && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  {errors.oldPassword}
                </Text>
              )}
            </FormControl>

            {/* NEW PASSWORD */}
            <FormControl mb={4} isInvalid={!!errors.newPassword}>
              <FormLabel fontSize="sm" color={textPrimary}>
                New Password
              </FormLabel>
              <InputGroup>
                <Input
                  type={showNew ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setErrors({ ...errors, newPassword: '' });
                  }}
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={() => setShowNew(!showNew)}>
                    {showNew ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              {errors.newPassword && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  {errors.newPassword}
                </Text>
              )}
            </FormControl>

            {/* CONFIRM PASSWORD */}
            <FormControl mb={6} isInvalid={!!errors.confirmPassword}>
              <FormLabel fontSize="sm" color={textPrimary}>
                Confirm Password
              </FormLabel>
              <InputGroup>
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrors({ ...errors, confirmPassword: '' });
                  }}
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              {errors.confirmPassword && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  {errors.confirmPassword}
                </Text>
              )}
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              w="100%"
              mb={4}
              isLoading={isPending}
              loadingText="Resetting..."
            >
              Reset Password
            </Button>

            <Button
              variant="ghost"
              colorScheme="blue"
              w="100%"
              onClick={() => navigate('/auth/sign-in')}
            >
              Back to Login
            </Button>
          </form>
        </Box>
      </Flex>
    </DefaultAuth>
  );
}

export default ResetPassword;
